// Offers as an outside client keeps them: its own advertisers' links, the GEO
// each covers, the payout. The full Offers page (pools, coverage, audits,
// exports) is ours; a client's tracker routes only to the offers on this list.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createOffer, deleteOffer, getOffers, updateOffer } from '@/api/offers';
import type { Offer, OfferCurrency } from '@/api/types';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useAuthStore } from '@/store/auth';

const inputClass = 'w-full px-3 py-2 text-sm rounded-md border border-border bg-surface-muted text-content';

interface FormState {
  partner: string;
  brand: string;
  geo: string;
  offer_url: string;
  payout: string;
  currency: OfferCurrency;
  cap: string;
  status: 'active' | 'paused';
}

const emptyForm: FormState = {
  partner: '', brand: '', geo: '', offer_url: '', payout: '', currency: 'USD', cap: '', status: 'active',
};

function formOf(o: Offer): FormState {
  return {
    partner: o.partner,
    brand: o.brand ?? '',
    geo: o.geo,
    offer_url: o.offer_url ?? '',
    payout: o.payout_cents == null ? '' : String(o.payout_cents / 100),
    currency: o.currency,
    cap: o.cap == null ? '' : String(o.cap),
    status: o.status,
  };
}

export function ClientOffers() {
  const qc = useQueryClient();
  const canEdit = useAuthStore((s) => s.canEdit());
  const offersQ = useQuery({ queryKey: ['offers'], queryFn: getOffers });
  const [editing, setEditing] = useState<Offer | null>(null);
  const [creating, setCreating] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['offers'] });
  const remove = useMutation({ mutationFn: (id: string) => deleteOffer(id), onSuccess: refresh });
  const offers = offersQ.data ?? [];

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-content">Offers</h1>
          <p className="mt-1 text-sm text-content-muted">
            Your advertisers' links. Put <code className="text-xs">{'{click_id}'}</code> into the URL and have the
            advertiser send it back in the postback (Tracker → Campaigns shows the postback URL).
          </p>
        </div>
        {canEdit && <Button onClick={() => setCreating(true)}>New offer</Button>}
      </div>

      {offersQ.isLoading ? (
        <p className="text-sm text-content-muted">Loading…</p>
      ) : offers.length === 0 ? (
        <section className="rounded-lg border border-dashed border-border bg-surface/40 p-8 text-center">
          <p className="font-medium text-content">No offers yet</p>
        </section>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-content-subtle">
                <th className="px-3 py-2 font-normal">Advertiser</th>
                <th className="px-3 py-2 font-normal">GEO</th>
                <th className="px-3 py-2 font-normal">Payout</th>
                <th className="px-3 py-2 font-normal">Cap</th>
                <th className="px-3 py-2 font-normal">Clicks / Regs / Deps</th>
                <th className="px-3 py-2 font-normal">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {offers.map((o) => (
                <tr key={o.id} className="border-t border-border align-top">
                  <td className="px-3 py-2">
                    <div className="text-content">{o.brand ? `${o.brand} · ${o.partner}` : o.partner}</div>
                    {o.offer_url && <div className="max-w-xs truncate text-xs text-content-subtle" title={o.offer_url}>{o.offer_url}</div>}
                  </td>
                  <td className="px-3 py-2 text-content-muted">{o.geo}</td>
                  <td className="px-3 py-2 text-content-muted">
                    {o.payout_cents == null ? '—' : `${(o.payout_cents / 100).toFixed(2)} ${o.currency}`}
                  </td>
                  <td className="px-3 py-2 text-content-muted">{o.cap ? `${o.cap_deps}/${o.cap}` : '—'}</td>
                  <td className="px-3 py-2 text-content-muted">{o.clicks} / {o.regs} / {o.deps}</td>
                  <td className="px-3 py-2">
                    <span className={o.status === 'active' ? 'text-success' : 'text-content-subtle'}>{o.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {canEdit && (
                      <>
                        <Button variant="secondary" size="sm" onClick={() => setEditing(o)}>Edit</Button>{' '}
                        <Button variant="secondary" size="sm" disabled={remove.isPending}
                          onClick={() => { if (confirm(`Delete offer ${o.partner} (${o.geo})?`)) remove.mutate(o.id); }}>
                          Delete
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {remove.error && <p className="text-sm text-danger">{(remove.error as Error).message}</p>}

      <OfferForm
        key={editing?.id ?? (creating ? 'new' : 'none')}
        isOpen={creating || !!editing}
        offer={editing}
        onClose={() => { setCreating(false); setEditing(null); }}
        onSaved={() => { setCreating(false); setEditing(null); void refresh(); }}
      />
    </div>
  );
}

function OfferForm({ isOpen, offer, onClose, onSaved }: {
  isOpen: boolean; offer: Offer | null; onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState<FormState>(offer ? formOf(offer) : emptyForm);
  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));
  const save = useMutation({
    mutationFn: async () => {
      const payout = form.payout.trim() === '' ? null : Math.round(Number(form.payout) * 100);
      const data = {
        partner: form.partner.trim(),
        brand: form.brand.trim() || null,
        geo: form.geo.trim().toUpperCase(),
        offer_url: form.offer_url.trim() || null,
        payout_cents: payout,
        currency: form.currency,
        cap: form.cap.trim() === '' ? null : Number(form.cap),
        status: form.status,
      };
      return offer ? updateOffer(offer.id, data) : createOffer(data);
    },
    onSuccess: onSaved,
  });
  const valid = form.partner.trim() && form.geo.trim() && (form.payout.trim() === '' || Number.isFinite(Number(form.payout)));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={offer ? 'Edit offer' : 'New offer'} size="lg">
      <form className="grid gap-3 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); if (valid) save.mutate(); }}>
        <label className="text-sm text-content-muted">Advertiser / network
          <input value={form.partner} onChange={(e) => set({ partner: e.target.value })} className={inputClass} />
        </label>
        <label className="text-sm text-content-muted">Brand (optional)
          <input value={form.brand} onChange={(e) => set({ brand: e.target.value })} className={inputClass} />
        </label>
        <label className="text-sm text-content-muted sm:col-span-2">Offer URL
          <input value={form.offer_url} onChange={(e) => set({ offer_url: e.target.value })}
            placeholder="https://advertiser.example/?sub1={click_id}" className={`${inputClass} font-mono`} />
        </label>
        <label className="text-sm text-content-muted">GEO (ISO codes, comma-separated)
          <input value={form.geo} onChange={(e) => set({ geo: e.target.value })} placeholder="BR,MX" className={inputClass} />
        </label>
        <div className="grid grid-cols-[1fr_6rem] gap-2">
          <label className="text-sm text-content-muted">Payout per deposit
            <input value={form.payout} onChange={(e) => set({ payout: e.target.value })} inputMode="decimal" className={inputClass} />
          </label>
          <label className="text-sm text-content-muted">Currency
            <select value={form.currency} onChange={(e) => set({ currency: e.target.value as OfferCurrency })} className={inputClass}>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
          </label>
        </div>
        <label className="text-sm text-content-muted">Cap (deposits, optional)
          <input value={form.cap} onChange={(e) => set({ cap: e.target.value })} inputMode="numeric" className={inputClass} />
        </label>
        <label className="text-sm text-content-muted">Status
          <select value={form.status} onChange={(e) => set({ status: e.target.value as 'active' | 'paused' })} className={inputClass}>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
          </select>
        </label>
        {save.error && <p className="sm:col-span-2 text-sm text-danger">{(save.error as Error).message}</p>}
        <div className="sm:col-span-2 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={save.isPending} disabled={!valid}>{offer ? 'Save' : 'Create'}</Button>
        </div>
      </form>
    </Modal>
  );
}
